import type { Widget } from '@app/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  ArrowUp,
  Check,
  ImagePlus,
  RotateCcw,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { AssistantApplyResult, AssistantConfig } from '../lib/assistant'
import { assistantFailure, TURN_MISSING } from '../lib/assistant-failure'
import { stageDraft } from '../lib/message-draft'
import { useTRPC } from '../lib/trpc'
import { AssistantModelPin, AssistantModels } from './AssistantModels'
import { AssistantWidget } from './AssistantWidgets'
import { FormatView } from './FormatView'
import { Button, Notice, Textarea } from './index'
import { Tooltip } from './Tooltip'
import './assistant.css'

/**
 * The page assistant.
 *
 * A VIEW of the companion band, not a band of its own. It is one of the things
 * that accompany work rather than interrupt it, so it shares the one strip of
 * width the shell opens — which is also what stops it and the page's own views
 * competing for the same edge. The band carries the width, the way out and the
 * strip that says which view you are on; everything here is the conversation.
 *
 * The band does not dim the page behind it, and deliberately: its entire
 * justification is reading a proposed change AGAINST the rows it would change,
 * and a scrim heavy enough to focus you is heavy enough to stop you doing that.
 *
 * The transcript is held here, in the component, and sent back each turn. That
 * is enough for the surface to work and keeps the first version honest about
 * what it is — nothing is persisted, so nothing has to be retained or expired
 * yet. Server-side conversations arrive with the telemetry work.
 *
 * The model returns prose and, at most, a proposal. It never returns markup:
 * what a change looks like is decided here, with the app's own components, so
 * an assistant that starts hallucinating layout cannot reach the screen.
 */
/**
 * A block of text shown line by line, each in its own direction.
 *
 * `dir="auto"` lets the browser decide from the line's own first strong
 * character, which is exactly right for an invitation that mixes scripts — and
 * `unicode-bidi: isolate` stops one line's direction leaking into the next.
 * Rendering the whole thing as one string with `pre-wrap` is what produced an
 * announcement whose lines were in an order nobody wrote.
 */
function Lines({
  text,
  centered = false,
}: {
  text: string
  centered?: boolean
}) {
  // Keyed by the line and which occurrence of it this is — no index, and stable
  // when the text is re-rendered. An announcement really can repeat a line (two
  // grandparent couples, a blank between blocks), so content alone is not
  // enough on its own.
  const seen = new Map<string, number>()
  const lines = text.split('\n').map((line) => {
    const nth = (seen.get(line) ?? 0) + 1
    seen.set(line, nth)
    return { key: `${nth}\u0000${line}`, line }
  })

  return (
    <>
      {lines.map(({ key, line }) => (
        <span
          key={key}
          className={`asst__line${centered ? ' asst__line--centered' : ''}`}
          dir="auto"
        >
          {line}
        </span>
      ))}
    </>
  )
}

function printedValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && 'time' in value) {
    return String((value as { time: unknown }).time)
  }
  return String(value)
}

/** The actual source values an unsaved HTML page will print once confirmed. */
function PageProposalTimes({
  spaceId,
  operation,
}: {
  spaceId: string
  operation: { args: Record<string, unknown> }
}) {
  const trpc = useTRPC()
  const body = String(operation.args.body ?? '')
  const onDate = String(operation.args.onDate ?? '')
  const preview = useQuery(
    trpc.modaah.previewDraft.queryOptions({ spaceId, body, onDate }),
  )
  if (preview.isPending)
    return <div className="asst__reads">Checking times…</div>
  if (preview.isError) {
    return (
      <Notice tone="error">The page’s times could not be previewed.</Notice>
    )
  }
  const sources = Object.entries(preview.data.sources)
  return (
    <div className="asst__request">
      <div className="asst__proposal-title">Times this page will print</div>
      {sources.length ? (
        <div className="asst__ops">
          {sources.map(([name, value]) => (
            <div key={name}>
              {name} — {printedValue(value)}
            </div>
          ))}
        </div>
      ) : (
        <div className="asst__proposal-summary">No times.</div>
      )}
      {preview.data.unavailableSources.length ? (
        <Notice tone="error">
          Unavailable: {preview.data.unavailableSources.join(', ')}
        </Notice>
      ) : null}
    </div>
  )
}

export function Assistant({
  skill,
  spaceId,
  /** Who is speaking, over a streaming turn — and the name the band's strip
   *  gives this view, so the two cannot say different things. */
  title,
  invalidateOnApply,
  opening,
  localFontFamilies,
  onApplied,
}: AssistantConfig) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const navigate = useNavigate()
  /**
   * Opening starts a NEW conversation.
   *
   * Reopening the panel is how someone begins a fresh thought, not how they
   * resume an old one — dropping them back into last week's half-finished
   * exchange means reading it to work out where they are before they can say
   * anything. An earlier conversation is something you go to on purpose, and
   * needs a place to go to — the record is kept, and no surface reads it yet.
   *
   * The id is still held for the session so a REFRESH resumes rather than
   * abandoning a conversation that cost real money — a reload is an accident,
   * reopening is a decision.
   */
  const [conversationId] = useState(() => crypto.randomUUID())

  const [history, setHistory] = useState<unknown[]>([])
  const [shown, setShown] = useState<
    {
      /** `note` is the panel speaking about itself — which model answered, and why. */
      who: 'you' | 'assistant' | 'note'
      text: string
      reads?: string[]
      widgets?: Widget[]
    }[]
  >([])
  const [proposal, setProposal] = useState<{
    id: string
    summary: string
    operations: { tool: string; args: Record<string, unknown> }[]
  } | null>(null)
  /**
   * Widgets that have arrived but whose turn has not settled yet.
   *
   * Shown live for the same reason the lookups are: a table the assistant spent
   * a step building should appear when it exists, not after whatever it decides
   * to say about it. Cleared into the transcript when the turn lands, so nothing
   * is on screen twice.
   */
  const [liveWidgets, setLiveWidgets] = useState<Widget[]>([])
  const [draft, setDraft] = useState('')
  /**
   * An attached invitation waits for Send rather than firing on choose. The
   * whole point is that the person can say what they want done with it — "put
   * this on the Shabbos list", "send it tonight" — in the same breath.
   */
  const [pending, setPending] = useState<{
    name: string
    dataUrl: string
  } | null>(null)
  const [composed, setComposed] = useState<{
    subject: string
    body: string
    bodyHtml: string
    sms: string
    unreadable: string[]
  } | null>(null)
  /** The assistant's own note, set when it judges the handoff ready. */
  const [offer, setOffer] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  /** The composer's options, closed until somebody reaches for them. */
  const [options, setOptions] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const logRef = useRef<HTMLDivElement>(null)

  /**
   * Counted rather than a boolean: dragging over a child fires `dragleave` on
   * the parent, so a flag flickers off the moment the pointer crosses anything
   * inside the panel.
   */
  const [dropDepth, setDropDepth] = useState(0)

  /* Anything arriving scrolls the log to the foot. The three are read in the
     guard rather than listed and ignored: a dependency the body never touches is
     one the rule is right to call unnecessary, because it cannot tell a trigger
     from a value. Reading them says which they are. */
  useEffect(() => {
    if (!shown.length && !proposal && !liveWidgets.length) return
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight })
  }, [shown.length, proposal, liveWidgets.length])

  /**
   * A turn arrives as a stream: text as it is written, and a note of each
   * lookup as it happens. The finished turn comes last and carries the history
   * and anything to confirm.
   *
   * `streaming` holds the text as it builds. It is separate from the settled
   * transcript so a half-written answer can never be mistaken for a finished
   * one — and so a failure part-way leaves nothing pretending to be complete.
   */
  const [streaming, setStreaming] = useState('')
  const [readingNow, setReadingNow] = useState<string[]>([])

  const chat = useMutation({
    ...trpc.agent.chat.mutationOptions(),
    onSuccess: async (stream) => {
      let text = ''
      const reads: string[] = []
      const seen: Widget[] = []
      // A stream can end cleanly without its settled turn — a connection closed
      // between frames reads as the end of the data. That is still a failure,
      // and the panel says so rather than falling quiet.
      let settled = false
      try {
        for await (const event of stream) {
          if (event.type === 'text') {
            text += event.delta
            setStreaming(text)
          } else if (event.type === 'reading') {
            reads.push(event.tool)
            setReadingNow([...reads])
          } else if (event.type === 'widget') {
            seen.push(event.widget as Widget)
            setLiveWidgets([...seen])
          } else if (event.type === 'notice') {
            // A pinned model gave way. Said in the transcript, where somebody
            // wondering why an answer reads differently is already looking —
            // and the picker below is refetched, because the pin is now off.
            setShown((s) => [...s, { who: 'note', text: event.text }])
            qc.invalidateQueries({ queryKey: trpc.agent.models.queryKey() })
          } else {
            const { turn } = event
            settled = true
            setHistory(turn.messages)
            setShown((s) => [
              ...s,
              {
                who: 'assistant',
                text: turn.text,
                reads: turn.readCalls,
                // The settled turn is authoritative — the live list is only
                // what happened to arrive before it.
                widgets: turn.widgets as Widget[],
              },
            ])
            setProposal(turn.proposal)
            if (turn.composed) setComposed(turn.composed)
            if (turn.offerComposer) setOffer(turn.offerComposer)
          }
        }
        if (!settled) setError(TURN_MISSING)
      } catch (e) {
        setError(assistantFailure(e))
      } finally {
        // Cleared only once the settled turn is in the transcript, so nothing
        // disappears and reappears.
        setStreaming('')
        setReadingNow([])
        setLiveWidgets([])
      }
    },
    onError: (e) => setError(assistantFailure(e)),
  })

  const openingSent = useRef(false)
  useEffect(() => {
    if (!opening || openingSent.current) return
    openingSent.current = true
    setShown([{ who: 'you', text: opening }])
    chat.mutate({
      spaceId,
      conversationId,
      skill,
      messages: [{ role: 'user', content: opening }] as never,
      localFontFamilies,
    })
  }, [chat, conversationId, localFontFamilies, opening, skill, spaceId])

  const markUsed = useMutation(trpc.agent.markUsed.mutationOptions())

  /**
   * A proposal that is one feature request, and nothing else.
   *
   * Read off the operation rather than flagged by the model, so the panel
   * cannot be told it is showing a request while it is holding something else.
   * A mixed proposal falls through to the ordinary rendering: it would be a
   * confusing thing for the assistant to have built, and dressing it up as a
   * request would hide whatever else was in it.
   */
  const featureRequest =
    proposal?.operations.length === 1 &&
    proposal.operations[0].tool === 'task_fileFeatureRequest'
      ? {
          title: String(proposal.operations[0].args.title ?? ''),
          description: String(proposal.operations[0].args.description ?? ''),
        }
      : null

  /** Start again — a new conversation, so the last one's outcome stands. */
  function startFresh() {
    // A reload is the simplest honest way to reset every piece of turn state at
    // once; anything else risks carrying one of them into the new conversation.
    window.location.reload()
  }

  const applyProposal = useMutation({
    ...trpc.agent.applyProposal.mutationOptions(),
    onSuccess: (res) => {
      setProposal(null)
      const failed = res.results.filter((r) => !r.ok)
      // A filed request answers with its key. It is the only receipt the
      // person gets — they cannot open the board to go and look.
      const filed = res.results.find((r) => r.ok && r.key)
      setShown((s) => [
        ...s,
        {
          who: 'assistant',
          text: failed.length
            ? `Applied ${res.results.length - failed.length} of ${res.results.length}. ${failed[0]?.error ?? ''}`
            : filed
              ? `Sent as ${filed.key}.`
              : 'Done.',
        },
      ])
      for (const key of invalidateOnApply ?? []) {
        qc.invalidateQueries({ queryKey: key as never })
      }
      onApplied?.(res.results as AssistantApplyResult[])
    },
    onError: (e) => setError(e.message),
  })

  function attach(file: File) {
    setError(null)
    if (!file.type.startsWith('image/')) {
      setError('That is not an image. Attach a photo or a screenshot of it.')
      return
    }
    const reader = new FileReader()
    reader.onload = () =>
      setPending({ name: file.name, dataUrl: String(reader.result) })
    reader.readAsDataURL(file)
  }

  const rejectProposal = useMutation({
    ...trpc.agent.rejectProposal.mutationOptions(),
    onSuccess: () => {
      setProposal(null)
      setShown((s) => [
        ...s,
        {
          who: 'assistant',
          text: featureRequest ? 'Not sent.' : 'Left as it was.',
        },
      ])
    },
    onError: (e) => setError(e.message),
  })

  /**
   * One path for everything the person says, whether they typed it or picked
   * it. A chip that took its own route to the server would be a second way of
   * saying something, and the two would drift.
   */
  function say(said: string) {
    setError(null)
    setShown((s) => [
      ...s,
      { who: 'you', text: pending ? `${said}\n(${pending.name})` : said },
    ])
    chat.mutate({
      spaceId,
      conversationId,
      skill,
      messages: [...history, { role: 'user', content: said }] as never,
      imageDataUrl: pending?.dataUrl,
      localFontFamilies,
    })
    setPending(null)
  }

  function send() {
    const text = draft.trim()
    // An attachment on its own is a message: the words are optional, the
    // opportunity to add them is the point.
    if ((!text && !pending) || chat.isPending) return
    setDraft('')
    say(text || 'Read this invitation.')
  }

  const busy = chat.isPending || applyProposal.isPending
  // Pending means the request is open; the panel is only truly waiting with
  // nothing to show when no text has arrived yet.
  const waiting = busy && !streaming

  return (
    <div
      className={`assistant${dropDepth > 0 ? ' assistant--dropping' : ''}`}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes('Files')) setDropDepth((d) => d + 1)
      }}
      onDragLeave={() => setDropDepth((d) => Math.max(0, d - 1))}
      onDragOver={(e) => {
        // Without this the browser navigates to the file instead of handing
        // it over, which loses the conversation.
        if (e.dataTransfer.types.includes('Files')) e.preventDefault()
      }}
      onDrop={(e) => {
        e.preventDefault()
        setDropDepth(0)
        const file = e.dataTransfer.files?.[0]
        if (file) attach(file)
      }}
    >
      {dropDepth > 0 && (
        <div className="assistant__drop">Drop the invitation here</div>
      )}

      <div className="assistant__body">
        <div className="asst__log" ref={logRef}>
          {shown.length === 0 && (
            <p className="asst__empty">
              Describe what you want in your own words. I’ll look at what’s
              already set up, ask anything I need, and show you the exact change
              before anything happens.
            </p>
          )}

          {shown.map((t, i) =>
            t.who === 'note' ? (
              <p className="asst__note" key={i}>
                {t.text}
              </p>
            ) : (
              <div
                key={i}
                className="asst__turn"
                data-role={t.who === 'you' ? 'user' : 'assistant'}
              >
                {t.reads?.length ? (
                  <span className="asst__reads">
                    <Check size={12} aria-hidden />
                    Looked at {t.reads.length}{' '}
                    {t.reads.length === 1 ? 'thing' : 'things'}
                  </span>
                ) : null}
                {/* What the assistant said is markdown; what the person typed
                    is plain text and is shown as they typed it. */}
                {t.text && t.who === 'assistant' ? (
                  <FormatView format="markdown" value={t.text} />
                ) : t.text ? (
                  <div className="asst__said">
                    {/* The words read in the direction of their first letter.
                        The pill around them does not: the side it sits
                        against and the corner its tail is on say who spoke,
                        whatever they wrote in. */}
                    <div dir="auto">{t.text}</div>
                  </div>
                ) : null}
                {t.widgets?.map((w, j) => (
                  <AssistantWidget
                    key={j}
                    widget={w}
                    spaceId={spaceId}
                    /*
                     * Granting the opt-in resumes the conversation rather than
                     * leaving it holding a refusal that is no longer true. It goes
                     * back as the person's own turn, because it is: they just
                     * changed the setting, and saying so is what makes the
                     * assistant look again instead of repeating itself.
                     */
                    onGranted={
                      i === shown.length - 1 && !busy
                        ? () =>
                            say(
                              'I have just allowed member details. Please try that again.',
                            )
                        : undefined
                    }
                    // Only the last thing said is still answerable. Anything
                    // above it is a question that has already been answered —
                    // the answer is the very next turn — so its chips are a
                    // record rather than a control.
                    onAnswer={i === shown.length - 1 && !busy ? say : undefined}
                  />
                ))}
              </div>
            ),
          )}

          {readingNow.length > 0 && (
            <div className="asst__reads">reading {readingNow.join(', ')}…</div>
          )}

          {/* Live, before the turn settles. Not answerable yet: the model is
              still mid-turn and may not have finished asking. */}
          {liveWidgets.map((w, i) => (
            <AssistantWidget key={i} widget={w} spaceId={spaceId} />
          ))}

          {streaming && (
            <div className="asst__turn" data-role="assistant">
              <div className="asst__who">{title}</div>
              <FormatView format="markdown" value={streaming} />
            </div>
          )}

          {waiting && <div className="asst__thinking">Working…</div>}

          {composed && (
            <div className="asst__proposal">
              <div className="asst__proposal-title">
                Read from the image — check it before sending.
              </div>
              <div className="asst__field">
                <span className="asst__field-label">Subject</span>
                <Lines text={composed.subject} />
              </div>
              <div className="asst__field">
                <span className="asst__field-label">Email</span>
                {/* Centered because that is how it SENDS — an invitation is a
                    centered document, and a preview that flushes it left is a
                    preview of something else. */}
                <Lines text={composed.body} centered />
              </div>
              <div className="asst__field">
                <span className="asst__field-label">Text</span>
                <Lines text={composed.sms} />
              </div>
              {composed.unreadable.length > 0 && (
                <Notice tone="error">
                  Could not read: {composed.unreadable.join(', ')}. Fill these
                  in before sending rather than leaving them to guesswork.
                </Notice>
              )}
            </div>
          )}

          {/* Offered by the assistant, not by the panel — and only once it has
              the list and the timing. A button that appears the moment an
              invitation is read invites skipping the two questions the draft
              cannot answer for itself. */}
          {offer && composed && (
            <div className="asst__proposal">
              <div className="asst__proposal-title">{offer}</div>
              <div className="asst__actions">
                <Button
                  onClick={() => {
                    stageDraft({
                      subject: composed.subject,
                      bodyHtml: composed.bodyHtml,
                      bodyText: composed.body,
                      sms: composed.sms,
                      source: 'an invitation image',
                      conversationId,
                    })
                    // Opening is a weaker signal than sending, and it is
                    // recorded as such: the composer upgrades it if a message
                    // actually goes out.
                    markUsed.mutate({
                      spaceId,
                      conversationId,
                      via: 'composer_opened',
                    })
                    navigate({ to: '/messaging/new' })
                  }}
                >
                  Open in composer
                </Button>
              </div>
            </div>
          )}

          {proposal && (
            <div className="asst__proposal">
              <div className="asst__proposal-title">
                {featureRequest
                  ? 'Nothing has been sent yet. This is the feature request that would go:'
                  : 'Nothing has changed yet. This is what would happen:'}
              </div>
              {featureRequest ? (
                /*
                 * The one operation whose ARGUMENTS are what is being agreed
                 * to. Every other change is a structured edit the summary
                 * describes in words; this is a piece of writing that will be
                 * published somewhere the person cannot go and read it, so
                 * showing them its name instead would be asking them to
                 * approve something they have not seen.
                 */
                <div className="asst__request">
                  <div className="asst__request-title" dir="auto">
                    {featureRequest.title}
                  </div>
                  {/* A description is markdown, and the board draws it as
                      such — so it is drawn here the same way, and what is
                      agreed to is what will be read. */}
                  <FormatView
                    format="markdown"
                    value={featureRequest.description}
                  />
                  <p className="asst__request-note">
                    This conversation goes with it, so whoever picks it up can
                    see what you were trying to do.
                  </p>
                </div>
              ) : (
                <>
                  <div className="asst__proposal-summary">
                    {proposal.summary}
                  </div>
                  <div className="asst__ops">
                    {proposal.operations.map((op, i) => (
                      <div key={i}>{op.tool}</div>
                    ))}
                  </div>
                  {proposal.operations
                    .filter((op) => op.tool === 'modaah_create')
                    .map((op, i) => (
                      <PageProposalTimes
                        key={i}
                        spaceId={spaceId}
                        operation={op}
                      />
                    ))}
                </>
              )}
              <div className="asst__actions">
                <Button
                  onClick={() =>
                    applyProposal.mutate({ spaceId, proposalId: proposal.id })
                  }
                  disabled={busy}
                >
                  {/* The verb says what the button does. "Apply" is right for a
                      change to this org's own data and wrong for a message
                      going somewhere else. */}
                  {featureRequest ? 'Send it' : 'Apply'}
                </Button>
                <Button
                  variant="quiet"
                  onClick={() =>
                    rejectProposal.mutate({
                      spaceId,
                      proposalId: proposal.id,
                    })
                  }
                  disabled={busy}
                >
                  {featureRequest ? "Don't send" : 'Not this'}
                </Button>
              </div>
            </div>
          )}

          {error && <Notice tone="error">{error}</Notice>}
        </div>
      </div>

      <div className="assistant__foot">
        <div className="asst__composer">
          {options && <AssistantModels />}

          {pending && (
            <div className="asst__attached">
              <span>{pending.name}</span>
              <button
                type="button"
                className="asst__attached-x"
                onClick={() => setPending(null)}
                aria-label="Remove attachment"
              >
                <X size={12} aria-hidden />
              </button>
            </div>
          )}

          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              pending ? 'Say what to do with it — or just send' : 'Ask anything'
            }
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter is a newline. A chat box that needs a
              // button click for every turn is a form pretending to be a
              // conversation.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                send()
              }
            }}
          />

          <div className="asst__composer-bar">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) attach(f)
                e.target.value = ''
              }}
            />
            <div className="asst__composer-tools">
              {/* Starting over is a thing you do to THIS conversation, so it
                  sits with the conversation's own tools. The band's head
                  carries the strip and the way out, and nothing per-view. */}
              {shown.length > 0 && (
                <Tooltip label="Start again">
                  <Button
                    variant="quiet"
                    onClick={startFresh}
                    disabled={busy}
                    aria-label="Start again"
                  >
                    <RotateCcw size={16} aria-hidden />
                  </Button>
                </Tooltip>
              )}
              <Button
                variant="quiet"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                aria-label="Attach an invitation image"
              >
                <ImagePlus size={16} aria-hidden />
              </Button>
              <Button
                variant="quiet"
                onClick={() => setOptions((o) => !o)}
                data-open={options ? '' : undefined}
                aria-expanded={options}
                aria-label="Assistant options"
              >
                <SlidersHorizontal size={16} aria-hidden />
              </Button>
              {!options && <AssistantModelPin />}
            </div>
            <Button
              onClick={send}
              disabled={busy || (!draft.trim() && !pending)}
              aria-label="Send"
            >
              <ArrowUp size={16} aria-hidden />
            </Button>
          </div>
        </div>

        <p className="asst__caution">
          The assistant can be wrong. Read the change before you apply it.
        </p>
      </div>
    </div>
  )
}
